import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { ExportButton, MarkdownExport } from '../components/ExportButtons.js';
import { GradeBadge, useLocale } from '../components/site.js';
import { Button, LinkButton, LoadError, Loading, Notice } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDateTime, formatNumber } from '../i18n.js';
import { useMe } from '../session.js';

const URGENCY_STYLE: Record<string, string> = {
  now: 'bg-red-100 text-red-900',
  soon: 'bg-orange-100 text-orange-950',
  when_possible: 'bg-amber-100 text-amber-950',
  tip: 'bg-slate-100 text-slate-800',
};

/** A report section: a heading with a rule, kept together on paper where it fits. */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3 border-t border-slate-300 pt-5">
      <h2 className="text-xl font-bold">{title}</h2>
      {children}
    </section>
  );
}

/**
 * Report of one site (T-43): state, areas, open problems with what to do, speed and AI search on
 * one page that prints (or saves as PDF) without the app around it. Not on the Free plan.
 */
export function Report() {
  const { siteId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const timeZone = me.data?.account.timezone;
  const allowed = me.data ? me.data.account.planCode !== 'free' : null;
  const lang = i18n.language;

  const overview = useQuery({
    queryKey: ['site', siteId, 'overview', locale],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/overview', { params: { path: { siteId }, query: { locale } } }),
      ),
  });
  const problems = useQuery({
    queryKey: ['site', siteId, 'findings', 'grouped', locale],
    enabled: allowed === true,
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/findings', {
          params: { path: { siteId }, query: { grouped: 'true', status: 'open', locale } },
        }),
      ),
  });
  const perf = useQuery({
    queryKey: ['site', siteId, 'performance', 'report'],
    enabled: allowed === true,
    queryFn: () => unwrap(api.GET('/sites/{siteId}/performance', { params: { path: { siteId } } })),
  });
  const ai = useQuery({
    queryKey: ['site', siteId, 'ai-visibility', 'report'],
    enabled: allowed === true,
    queryFn: () =>
      unwrap(api.GET('/sites/{siteId}/ai-visibility', { params: { path: { siteId } } })),
  });

  if (overview.isPending || me.isPending) return <Loading />;
  if (overview.isError)
    return (
      <LoadError
        message={errorMessage(overview.error, t)}
        onRetry={() => void overview.refetch()}
      />
    );
  const o = overview.data;
  const site = o.site;
  const mobile = perf.data?.latest.find((s) => s.strategy === 'mobile') ?? null;
  const groups = problems.data?.groups ?? [];
  const counts = { now: 0, soon: 0, when_possible: 0, tip: 0 } as Record<string, number>;
  for (const g of groups) {
    const u = g.explanation?.urgency ?? 'tip';
    counts[u] = (counts[u] ?? 0) + g.count;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <p className="max-w-prose text-slate-700">{t('report.intro')}</p>
        <div className="flex flex-wrap items-center gap-2">
          <ExportButton siteId={siteId} format="csv" />
          <MarkdownExport siteId={siteId} />
          {allowed ? <Button onClick={() => window.print()}>{t('report.print')}</Button> : null}
        </div>
      </div>

      {allowed === false ? (
        <Notice kind="info">
          {t('report.planNeeded')}{' '}
          <LinkButton to="/billing" variant="soft">
            {t('report.upgrade')}
          </LinkButton>
        </Notice>
      ) : null}

      {allowed ? (
        <article className="flex flex-col gap-6 rounded-lg border border-slate-200 bg-white p-5 sm:p-8 print:border-0 print:p-0">
          <header className="flex flex-col gap-1">
            <p className="text-sm font-semibold tracking-wide text-blue-800 uppercase">
              {t('app.name')} · {t('report.title')}
            </p>
            <h1 className="text-2xl font-bold break-all sm:text-3xl">{site.name}</h1>
            <p className="break-all text-slate-700">{site.url}</p>
            <p className="text-sm text-slate-700">
              {t('report.createdAt', {
                time: formatDateTime(new Date().toISOString(), lang, timeZone),
              })}
            </p>
          </header>

          <Section title={t('report.summary')}>
            {o.health ? (
              <div className="flex flex-wrap items-center gap-4">
                <GradeBadge grade={o.health.grade} score={o.health.score} />
                <p className="text-lg font-semibold">{o.health.label}</p>
              </div>
            ) : (
              <p>{t('health.pending')}</p>
            )}
            <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {(['now', 'soon', 'when_possible', 'tip'] as const).map((u) => (
                <li key={u} className={`rounded-md px-3 py-2 ${URGENCY_STYLE[u]}`}>
                  <span className="block text-2xl font-bold tabular-nums">{counts[u]}</span>
                  <span className="text-sm font-semibold">{t(`report.urgency.${u}`)}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title={t('report.areas')}>
            <table className="w-full border-collapse text-left">
              <tbody>
                {o.tiles.map((tile) => (
                  <tr key={tile.key} className="border-b border-slate-200 align-top">
                    <th scope="row" className="w-2/5 py-2 pr-3 text-left">
                      <span className="block font-semibold">{t(`site.tiles.${tile.key}`)}</span>
                      <span className="block text-sm font-normal text-slate-700">
                        {t(`tileStatus.${tile.status}`)}
                      </span>
                    </th>
                    <td className="py-2 wrap-anywhere">
                      {tile.headline}
                      {tile.sub ? (
                        <span className="block text-sm text-slate-700">{tile.sub}</span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title={t('report.problems')}>
            {problems.isPending ? <Loading /> : null}
            {problems.data && groups.length === 0 ? <p>{t('findings.empty.open')}</p> : null}
            <ol className="flex flex-col gap-4 wrap-anywhere">
              {groups.map((g) => (
                <li key={g.ruleCode} className="flex flex-col gap-1 print:break-inside-avoid">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-sm font-semibold ${URGENCY_STYLE[g.explanation?.urgency ?? 'tip']}`}
                    >
                      {t(`report.urgency.${g.explanation?.urgency ?? 'tip'}`)}
                    </span>
                    <h3 className="font-semibold">{g.title}</h3>
                    {g.count > 1 ? (
                      <span className="text-sm text-slate-700">
                        {t('findings.count', { count: g.count })}
                      </span>
                    ) : null}
                  </div>
                  {g.explanation ? (
                    <>
                      <p className="text-slate-800">{g.explanation.what}</p>
                      <p>
                        <span className="font-semibold">{t('findings.action')}</span>{' '}
                        {g.explanation.action}
                      </p>
                    </>
                  ) : null}
                </li>
              ))}
            </ol>
          </Section>

          <div className="grid gap-6 sm:grid-cols-2">
            <Section title={t('report.speed')}>
              {mobile ? (
                <dl className="grid grid-cols-2 gap-2">
                  <dt className="text-slate-700">{t('speed.score')}</dt>
                  <dd className="font-semibold tabular-nums">
                    {mobile.score !== null ? `${mobile.score} / 100` : '–'}
                  </dd>
                  <dt className="text-slate-700">LCP</dt>
                  <dd className="font-semibold tabular-nums">
                    {(mobile.field?.lcpMs ?? mobile.lab.lcpMs) !== null
                      ? `${formatNumber((mobile.field?.lcpMs ?? mobile.lab.lcpMs ?? 0) / 1000, lang)} s`
                      : '–'}
                  </dd>
                </dl>
              ) : (
                <p>{t('speed.none')}</p>
              )}
            </Section>
            <Section title={t('report.ai')}>
              {ai.data?.score != null ? (
                <p className="text-lg">
                  <span className="text-2xl font-bold tabular-nums">{ai.data.score}</span> / 100
                  {ai.data.blockedSearch.length > 0 ? (
                    <span className="block text-sm text-slate-700">
                      {t('report.aiBlocked', { bots: ai.data.blockedSearch.join(', ') })}
                    </span>
                  ) : null}
                </p>
              ) : (
                <p>{t('ai.none')}</p>
              )}
            </Section>
          </div>

          <footer className="border-t border-slate-300 pt-4 text-sm text-slate-700">
            {t('report.footer')}
          </footer>
        </article>
      ) : null}
    </div>
  );
}
